import { useMemo } from 'react';
import { gql } from '@apollo/client';
import {
  useDataTransforms,
  useFarmName,
  useStartYear,
  useYearsArray,
} from '../utils/hooks';
import useFarmOrSectionGroupData from '../hooks/useFarmOrSectionGroupData';
import ItemsTable from '../components/tables/ItemsTable';
import { getCropNames } from '../utils/farmData';
import MetricsSummary from '../components/economics/MetricsSummary';
import chartTypes from '../constants/chartTypes';
import { sum } from '../utils/aggregators';
import { asCurrency, asYearString } from '../utils/formatters';
import MetricsHeader from '../components/layout/MetricsHeader';
import SummarySection from '../components/layout/SummarySection';
import DetailsSection from '../components/layout/DetailsSection';
import { useFilterContext } from '../contexts/FilterContext';
import Content from '../components/layout/Content';
import { ANNUAL_MULTI_YEAR, N_YEARS } from '../constants/years';
import { ASSIGNMENTS } from '../constants/operations';
import { CropInputAggregateFragment } from '../queries';
import { getInputsForYear, metricsForYear } from '../utils/inputs';
import { isCropFilterValid } from '../utils/filters';

const QUERY_NAME = 'InputsEconQuery';
const FRAGMENT_NAME = 'InputsEconFragment';

const InputsFragment = gql`
  ${CropInputAggregateFragment}
  fragment ${FRAGMENT_NAME} on SectionsAggregate {
    inputAggregates(assignment: $assignment) {
      ...InputAggregateFields
    }
    ...CropInputAggregateFragment
  }
`;

const arrayTransform = (data) => data.map(({ displayCost }) => displayCost);

const InputsView = ({
  assignment = ASSIGNMENTS.OWNER,
  tableHeader = 'Owner Assigned Operation Inputs',
}) => {
  const costBasisView = assignment === ASSIGNMENTS.DEVELOPER;
  const { msg, data } = useFarmOrSectionGroupData(
    InputsFragment,
    QUERY_NAME,
    FRAGMENT_NAME,
    [
      {
        name: 'assignment',
        typeString: 'String',
        value: assignment,
      },
    ],
  );

  const {
    state: { year: yearIndex, years, crop: cropFilter },
  } = useFilterContext();
  const allYears = useYearsArray(N_YEARS);
  const farmName = useFarmName();
  const farmStartYear = useStartYear();

  const getActiveInputs = useMemo(() => {
    if (yearIndex === ANNUAL_MULTI_YEAR) {
      return (unfilteredData) =>
        [...Array(years).keys()].reduce((acc, y) => {
          return acc.concat(
            getInputsForYear(y, cropFilter, farmStartYear)(unfilteredData),
          );
        }, []);
    }
    return getInputsForYear(yearIndex, cropFilter, farmStartYear);
  }, [yearIndex, cropFilter, farmStartYear, years]);

  const [crops = [], activeInputs = []] = useDataTransforms(data, [
    getCropNames,
    getActiveInputs,
  ]);

  const cropFilterValid = isCropFilterValid(data, cropFilter);

  return (
    <>
      <MetricsHeader
        showCropFilter
        crops={crops}
        showAnnualSingleYearSelect
        showAnnualMultiYearSelect={costBasisView}
        showChartButton={!costBasisView}
      >
        Inputs Summary: {allYears[yearIndex]}
      </MetricsHeader>
      <Content>
        {!costBasisView && (
          <SummarySection>
            <MetricsSummary
              metrics={[
                {
                  accessor: 'allItems',
                  aggregator: sum,
                  format: asCurrency,
                  displayName: 'Total Cost',
                  defaultShown: true,
                },
              ]}
              data={{ allItems: activeInputs }}
              chartType={chartTypes.ITEMIZED}
              arrayTransform={arrayTransform}
              msg={msg}
            />
          </SummarySection>
        )}
        <DetailsSection>
          <ItemsTable
            data={activeInputs}
            msg={msg}
            cols={[
              ...(yearIndex === ANNUAL_MULTI_YEAR
                ? [
                    {
                      displayString: 'Year',
                      accessor: 'year',
                    },
                  ]
                : []),
              ...metricsForYear(!cropFilterValid),
            ]}
            tableHeader={<h3>{tableHeader}</h3>}
            csvFileName={[
              'Inputs',
              ...([asYearString(yearIndex, years, allYears)] ?? []),
              ...(cropFilterValid ? [cropFilter] : []),
              farmName,
            ].join(' - ')}
          />
        </DetailsSection>
      </Content>
    </>
  );
};

export default InputsView;
